// Picking leave days on the sheet's calendar: how a tap changes the range, which
// days cannot be picked and how many working days a range holds.
import { eachDayOfInterval, format, getDay, parseISO } from "date-fns";
import type { Locale } from "date-fns";
import type { CompanyHoliday, HolidaySettings } from "features/company-holidays/companyHolidays.types";
import { dayKey, isLeaveRangeTooLong } from "features/leave-calendar/leaveCalendarDates";

/** The days picked so far, as YYYY-MM-DD. While `isPickingEnd` is true, the next tap chooses the last day. */
export interface PickedRange {
  start: string;
  end: string;
  isPickingEnd: boolean;
}

/**
 * How a tap changes the range. The first tap picks a one-day range and waits for the
 * last day. The next tap on that day or later sets the last day. Any other tap
 * (a third one, or a day before the first) starts a new range.
 */
export function tapDay(picked: PickedRange | null, day: string): PickedRange {
  if (picked?.isPickingEnd && day >= picked.start) {
    return { start: picked.start, end: day, isPickingEnd: false };
  }
  return { start: day, end: day, isPickingEnd: true };
}

/** Every day of the active holidays; the holidays answer also holds the switched-off ones. */
export function holidayDayKeys(holidays: CompanyHoliday[]): Set<string> {
  const days = new Set<string>();
  for (const holiday of holidays) {
    if (!holiday.isActive || holiday.startDate > holiday.endDate) continue;
    const holidayDays = eachDayOfInterval({ start: parseISO(holiday.startDate), end: parseISO(holiday.endDate) });
    holidayDays.forEach((day) => days.add(dayKey(day)));
  }
  return days;
}

/** A weekend the company does not work, or an active holiday. */
export function isOffDay(day: string, settings: HolidaySettings, holidayDays: Set<string>): boolean {
  const weekday = getDay(parseISO(day));
  if (weekday === 6 && settings.saturdayOff) return true;
  if (weekday === 0 && settings.sundayOff) return true;
  return holidayDays.has(day);
}

/** The calendar years the first and the last day of the range fall in. */
export function yearsOfRange(range: PickedRange): number[] {
  return [Number(range.start.slice(0, 4)), Number(range.end.slice(0, 4))];
}

/** The days of the range that are not off days. */
export function countWorkingDays(start: string, end: string, isOff: (day: string) => boolean): number {
  const days = eachDayOfInterval({ start: parseISO(start), end: parseISO(end) });
  return days.filter((day) => !isOff(dayKey(day))).length;
}

/** «lapkričio 9–13», «spalio 30 – lapkričio 2», or a single «lapkričio 9». */
export function rangeLabel(start: string, end: string, locale: Locale): string {
  const first = parseISO(start);
  const last = parseISO(end);
  const firstText = format(first, "MMMM d", { locale });
  if (start === end) return firstText;
  if (format(first, "yyyy-MM") === format(last, "yyyy-MM")) return `${firstText}–${format(last, "d", { locale })}`;
  return `${firstText} – ${format(last, "MMMM d", { locale })}`;
}

/** Translation key of what is wrong with the picked days (too many, or the first or last one is an off day), or null. */
export function rangeProblemKey(range: PickedRange, isOff: (day: string) => boolean): string | null {
  if (isLeaveRangeTooLong(range.start, range.end)) return "leave_calendar_max_days_error";
  if (isOff(range.start)) return "leave_calendar_start_off_day";
  if (isOff(range.end)) return "leave_calendar_end_off_day";
  return null;
}

/** Translation key of what is wrong with the times of a half-day or hourly leave, or null when they are fine. */
export function timesProblemKey(duration: string, startTime: string, endTime: string): string | null {
  if (duration === "full_day") return null;
  if (!startTime || !endTime) return "leave_calendar_half_day_times";
  // HH:mm strings compare in clock order.
  if (startTime >= endTime) return "leave_calendar_time_range_invalid";
  return null;
}
